/**
 * ErrorsPage.tsx — /app/errors — error groups (list / detail / resolve)
 *
 * Purpose:    Aggregated error groups from GET /v1/errors, expandable detail
 *             rows, and a resolve action per group.
 * Inputs:     errorsApi from @/lib/saas-telemetry
 * Outputs:    Error triage UI
 * Constraints: Dark-first; WCAG AA. Gateway route rolling out — 404/405/501
 *              renders <ComingOnline/>, never a crash.
 * SOT:        nsentry SaaS completion spec § G-WEB (errors panel)
 */
import { useEffect, useState } from 'react'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { ComingOnline } from '@/components/ComingOnline'
import { errorsApi, type ErrorGroup } from '@/lib/saas-telemetry'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

export function ErrorsPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [groups, setGroups] = useState<ErrorGroup[]>([])
  const [error, setError] = useState<SentryError | undefined>()
  const [expanded, setExpanded] = useState<string | null>(null)

  async function load() {
    setState('loading')
    const result = await errorsApi.list()
    if (!result.ok) {
      setError(result.error)
      setState('error')
      return
    }
    setGroups(result.value)
    setState(result.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  async function handleResolve(id: string) {
    await errorsApi.resolve(id)
    await load()
  }

  if (state === 'error' && isComingOnline(error)) {
    return (
      <div className="p-6 lg:p-8">
        <h1 className="mb-6 text-xl font-semibold text-brand-ink">Errors</h1>
        <ComingOnline feature="Error tracking" description="Grouped exceptions from your apps appear here — install the errors plugin snippet to start sending." />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      <h1 className="mb-6 text-xl font-semibold text-brand-ink">Errors</h1>

      <AsyncScreen state={state} error={error} onRetry={load} emptyMessage="No errors this month — nice and quiet">
        <ul className="space-y-3">
          {groups.map((g) => (
            <li key={g.id} className="rounded-xl border border-brand-subtle bg-brand-surface">
              <button
                type="button"
                onClick={() => setExpanded((cur) => (cur === g.id ? null : g.id))}
                aria-expanded={expanded === g.id}
                className="flex w-full items-center justify-between gap-4 p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-brand-ink">{g.message}</p>
                  <p className="mt-0.5 text-xs text-brand-muted">
                    {g.culprit ?? 'unknown source'}
                    {g.lastSeen && ` · last seen ${new Date(g.lastSeen).toLocaleString()}`}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <span className="rounded-full bg-brand-surface-2 px-2 py-0.5 text-xs font-medium text-brand-secondary">×{g.count.toLocaleString()}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${g.status === 'open' ? 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300' : 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300'}`}>
                    {g.status}
                  </span>
                </div>
              </button>
              {expanded === g.id && (
                <div className="border-t border-brand-subtle p-4">
                  <dl className="grid gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-brand-muted">First seen</dt>
                      <dd className="text-brand-secondary">{g.firstSeen ? new Date(g.firstSeen).toLocaleString() : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-brand-muted">Last seen</dt>
                      <dd className="text-brand-secondary">{g.lastSeen ? new Date(g.lastSeen).toLocaleString() : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-brand-muted">Occurrences</dt>
                      <dd className="text-brand-secondary">{g.count.toLocaleString()}</dd>
                    </div>
                  </dl>
                  {g.status === 'open' && (
                    <button
                      type="button"
                      onClick={() => handleResolve(g.id)}
                      className="mt-4 rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
                    >
                      Mark resolved
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </AsyncScreen>
    </div>
  )
}
